/**
 *
 * @module accessContext
 * @desc The AccessContext contains tokens which prove the client has
 * been granted authorization to use BAM services. Additionally, content feature
 * responses have the ability to be user and platform specific based on a given AccessContext.
 *
 */

import RefreshGrant from './refreshGrant';

/**
 *
 * @class AccessContext
 * @extends {RefreshGrant}
 * @implements {AccessToken}
 * @desc Represents the response from exchanging a token and provides
 * access context for subsequent service requests.
 * @note Also acts as a TokenRequestBuilder (via RefreshGrant) to refresh itself.
 *
 */
export default class AccessContext extends RefreshGrant {

    /**
     *
     * @param {Object} accessContextPackage
     *
     */
    constructor(accessContextPackage) {

        const { token, tokenType, refreshToken } = accessContextPackage;
        const { expiresIn, issuedAt, refreshAt } = accessContextPackage;

        super(refreshToken);

        /**
         *
         * @access public
         * @type {String}
         * @desc The access token issued by the authorization server.
         *
         */
        this.token = token;

        /**
         *
         * @access private
         * @type {String}
         * @desc Gets or sets the token type.
         *
         */
        this.tokenType = tokenType;

        /**
         *
         * @access public
         * @type {Number}
         * @desc The lifetime in seconds of the access token from the time that it was issued.
         *
         */
        this.expiresIn = expiresIn || 600;

        /**
         *
         * @access public
         * @type {Number}
         * @desc The timestamp of when the access token was issued (set by the SDK).
         * @note Returns the number of milliseconds elapsed since 1 January 1970 00:00:00 UTC.
         *
         */
        this.issuedAt = issuedAt || Date.now();

        /**
         *
         * @access public
         * @type {Number}
         * @desc The timestamp of when the access token expires (calculated by the SDK).
         * @note Based on issuedAt + expiresIn
         *
         */
        this.expiresAt = new Date(this.issuedAt + (this.expiresIn * 1000)).getTime();

        /**
         *
         * @access public
         * @type {Number}
         * @desc Determines when the access token should be refreshed.
         * @note Calculated by the SDK using issuedAt, expiredAt, and
         * the refresh threshold set in configuration.
         *
         */
        this.refreshAt = refreshAt || null;

    }

    /**
     *
     * @access public
     * @desc Convenience method, determines if the access token has expired.
     * @returns {Boolean}
     *
     */
    expired() {

        return this.expiresAt < Date.now();

    }

}



// WEBPACK FOOTER //
// ./src/services/token/accessContext.js